import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ActivityHistory, type ActivityRecord } from "./ActivityHistory";
import { api } from "./api";
import type { AppSettings } from "./types";
import "./progress.css";

type Period = "30days" | "90days" | "all";

type RawWeight = {
  id: string;
  measured_at: string;
  weight_kg: number;
  body_fat_percent: number | null;
  notes: string | null;
  source: "manual" | "gpt" | "import";
  created_at: string;
};

type WeightForm = {
  measuredAt: string;
  weightKg: string;
  bodyFatPercent: string;
  notes: string;
};

function localDateTimeValue(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function initialWeightForm(): WeightForm {
  return {
    measuredAt: localDateTimeValue(),
    weightKg: "",
    bodyFatPercent: "",
    notes: "",
  };
}

function dateValue(value: string): number {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function withinPeriod(value: string, period: Period): boolean {
  if (period === "all") return true;
  const time = dateValue(value);
  if (!time) return false;
  const days = period === "30days" ? 30 : 90;
  return time >= Date.now() - days * 24 * 60 * 60 * 1000;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function signed(value: number): string {
  if (Math.abs(value) < 0.05) return "±0.0kg";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}kg`;
}

function WeightChart({ records, target }: { records: RawWeight[]; target: number | null }) {
  const sorted = [...records].sort((a, b) => dateValue(a.measured_at) - dateValue(b.measured_at));
  if (sorted.length === 0) {
    return <div className="progress-chart-empty">この期間の体重記録はありません。</div>;
  }

  const width = 320;
  const height = 168;
  const left = 36;
  const right = 14;
  const top = 15;
  const bottom = 28;
  const values = sorted.map((item) => Number(item.weight_kg));
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (target !== null) {
    min = Math.min(min, target);
    max = Math.max(max, target);
  }
  if (max - min < 1) {
    min -= 0.5;
    max += 0.5;
  } else {
    min -= 0.3;
    max += 0.3;
  }
  const span = max - min;
  const x = (index: number) => sorted.length === 1
    ? (left + width - right) / 2
    : left + index * (width - left - right) / (sorted.length - 1);
  const y = (value: number) => top + (max - value) * (height - top - bottom) / span;
  const points = sorted.map((item, index) => `${x(index)},${y(Number(item.weight_kg))}`).join(" ");
  const firstDate = new Date(sorted[0].measured_at);
  const lastDate = new Date(sorted[sorted.length - 1].measured_at);
  const shortDate = (date: Date) => `${date.getMonth() + 1}/${date.getDate()}`;

  return (
    <div className="progress-chart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="体重推移グラフ">
        <line x1={left} y1={top} x2={left} y2={height - bottom} className="chart-axis" />
        <line x1={left} y1={height - bottom} x2={width - right} y2={height - bottom} className="chart-axis" />
        <line x1={left} y1={y((max + min) / 2)} x2={width - right} y2={y((max + min) / 2)} className="chart-grid" />
        {target !== null && target >= min && target <= max && (
          <>
            <line x1={left} y1={y(target)} x2={width - right} y2={y(target)} className="chart-target" />
            <text x={width - right} y={y(target) - 4} textAnchor="end" className="chart-target-label">目標 {target.toFixed(1)}</text>
          </>
        )}
        {sorted.length > 1 && <polyline points={points} className="chart-line" />}
        {sorted.map((item, index) => (
          <circle key={item.id} cx={x(index)} cy={y(Number(item.weight_kg))} r="3.7" className="chart-point" />
        ))}
        <text x={left - 5} y={top + 4} textAnchor="end" className="chart-label">{max.toFixed(1)}</text>
        <text x={left - 5} y={height - bottom + 4} textAnchor="end" className="chart-label">{min.toFixed(1)}</text>
        <text x={left} y={height - 8} className="chart-label">{shortDate(firstDate)}</text>
        <text x={width - right} y={height - 8} textAnchor="end" className="chart-label">{shortDate(lastDate)}</text>
      </svg>
    </div>
  );
}

export function ProgressPage() {
  const [weights, setWeights] = useState<RawWeight[]>([]);
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [period, setPeriod] = useState<Period>("30days");
  const [form, setForm] = useState<WeightForm>(initialWeightForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [weightResult, activityResult, settingsResult] = await Promise.all([
        api<{ ok: true; weights: RawWeight[] }>("/api/app/weights"),
        api<{ ok: true; activities: ActivityRecord[] }>("/api/app/activities"),
        api<{ ok: true; settings: AppSettings }>("/api/app/settings"),
      ]);
      setWeights(weightResult.weights);
      setActivities(activityResult.activities);
      setSettings(settingsResult.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : "変化の記録を読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredWeights = useMemo(
    () => weights.filter((item) => withinPeriod(item.measured_at, period)),
    [period, weights],
  );
  const filteredActivities = useMemo(
    () => activities.filter((item) => withinPeriod(item.occurred_at, period)),
    [activities, period],
  );
  const sortedWeights = useMemo(
    () => [...filteredWeights].sort((a, b) => dateValue(b.measured_at) - dateValue(a.measured_at)),
    [filteredWeights],
  );
  const latest = [...weights].sort((a, b) => dateValue(b.measured_at) - dateValue(a.measured_at))[0] ?? null;
  const chronological = [...filteredWeights].sort((a, b) => dateValue(a.measured_at) - dateValue(b.measured_at));
  const change = chronological.length > 1
    ? Number(chronological[chronological.length - 1].weight_kg) - Number(chronological[0].weight_kg)
    : null;
  const sevenDayWeights = latest
    ? weights.filter((item) => {
      const time = dateValue(item.measured_at);
      const latestTime = dateValue(latest.measured_at);
      return time <= latestTime && time >= latestTime - 7 * 24 * 60 * 60 * 1000;
    })
    : [];
  const sevenDayAverage = sevenDayWeights.length
    ? sevenDayWeights.reduce((sum, item) => sum + Number(item.weight_kg), 0) / sevenDayWeights.length
    : null;
  const activityMinutes = filteredActivities.reduce((sum, item) => sum + Number(item.duration_minutes ?? 0), 0);

  async function saveWeight(event: FormEvent) {
    event.preventDefault();
    setError("");
    setMessage("");
    const weightKg = Number(form.weightKg);
    if (!Number.isFinite(weightKg) || weightKg <= 0) {
      setError("体重を入力してください。");
      return;
    }
    const bodyFat = form.bodyFatPercent.trim() === "" ? null : Number(form.bodyFatPercent);
    if (bodyFat !== null && (!Number.isFinite(bodyFat) || bodyFat < 0 || bodyFat > 100)) {
      setError("体脂肪率を0〜100の範囲で入力してください。");
      return;
    }

    setSaving(true);
    try {
      const result = await api<{ ok: true; weight: RawWeight }>("/api/app/weights", {
        method: "POST",
        body: JSON.stringify({
          measuredAt: new Date(form.measuredAt).toISOString(),
          weightKg,
          bodyFatPercent: bodyFat,
          notes: form.notes.trim() || null,
        }),
      });
      setWeights((current) => [result.weight, ...current]);
      setForm(initialWeightForm());
      setShowForm(false);
      setMessage("体重を記録しました。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "体重を記録できませんでした。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="panel progress-hero">
        <div>
          <p className="eyebrow">PROGRESS</p>
          <h2>続いた日だけで変化を見る</h2>
          <p>毎日測れなくても大丈夫。記録がある日だけをつないで表示します。</p>
        </div>
        <button type="button" className="progress-add" onClick={() => setShowForm((current) => !current)}>
          {showForm ? "閉じる" : "体重を記録"}
        </button>
      </section>

      {showForm && (
        <form className="panel progress-form" onSubmit={saveWeight}>
          <div className="panel-heading">
            <div><p className="eyebrow">QUICK RECORD</p><h2>体重・体脂肪</h2></div>
            <span className="status-chip">未入力項目は空欄で可</span>
          </div>
          <div className="progress-form-grid">
            <label className="field progress-wide">
              <span>測定日時</span>
              <input type="datetime-local" value={form.measuredAt} onChange={(event) => setForm({ ...form, measuredAt: event.target.value })} required />
            </label>
            <label className="field">
              <span>体重（kg）</span>
              <input type="number" min="1" max="500" step="0.1" value={form.weightKg} onChange={(event) => setForm({ ...form, weightKg: event.target.value })} placeholder="例：72.4" required />
            </label>
            <label className="field">
              <span>体脂肪率（%）</span>
              <input type="number" min="0" max="100" step="0.1" value={form.bodyFatPercent} onChange={(event) => setForm({ ...form, bodyFatPercent: event.target.value })} placeholder="未入力でも可" />
            </label>
            <label className="field progress-wide">
              <span>メモ</span>
              <textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="朝、食後、むくみなど" maxLength={1000} />
            </label>
          </div>
          <button className="button primary" disabled={saving}>{saving ? "保存中…" : "体重を保存"}</button>
        </form>
      )}

      {(message || error) && <div className={`notice ${error ? "error" : ""}`}>{error || message}</div>}

      <section className="progress-metrics" aria-label="変化の集計">
        <article><span>最新体重</span><strong>{latest ? `${Number(latest.weight_kg).toFixed(1)}kg` : "未記録"}</strong><small>{latest ? formatDate(latest.measured_at) : "最初の記録を追加できます"}</small></article>
        <article><span>7日平均</span><strong>{sevenDayAverage === null ? "—" : `${sevenDayAverage.toFixed(1)}kg`}</strong><small>{sevenDayWeights.length ? `${sevenDayWeights.length}件から計算` : "記録がありません"}</small></article>
        <article><span>期間内の変化</span><strong>{change === null ? "—" : signed(change)}</strong><small>{filteredWeights.length}件の記録</small></article>
        <article><span>運動</span><strong>{filteredActivities.length}件</strong><small>{Math.round(activityMinutes)}分</small></article>
      </section>

      <section className="panel progress-chart-panel">
        <div className="panel-heading progress-chart-heading">
          <div><p className="eyebrow">WEIGHT TREND</p><h2>体重の推移</h2></div>
          <button type="button" className="progress-reload" onClick={() => void load()} disabled={loading}>更新</button>
        </div>
        <div className="progress-filters" aria-label="表示期間">
          {([["30days", "30日"], ["90days", "90日"], ["all", "すべて"]] as [Period, string][]).map(([value, label]) => (
            <button type="button" key={value} className={period === value ? "active" : ""} onClick={() => setPeriod(value)}>{label}</button>
          ))}
        </div>
        {loading ? <div className="progress-chart-empty">読み込んでいます…</div> : <WeightChart records={filteredWeights} target={settings?.targetWeightKg ?? null} />}
      </section>

      <section className="panel progress-history">
        <div className="panel-heading">
          <div><p className="eyebrow">HISTORY</p><h2>測定履歴</h2></div>
          <span className="progress-count">{sortedWeights.length}件</span>
        </div>
        {sortedWeights.length === 0 ? (
          <div className="progress-empty"><strong>この期間の記録はありません</strong><p>測れた日に一件だけ追加すれば十分です。</p></div>
        ) : (
          <div className="progress-list">
            {sortedWeights.map((item) => (
              <article key={item.id}>
                <div><time>{formatDate(item.measured_at)}</time><span>{item.source === "gpt" ? "GPT" : item.source === "import" ? "取込" : "手動"}</span></div>
                <strong>{Number(item.weight_kg).toFixed(1)}kg</strong>
                <small>{item.body_fat_percent === null ? "体脂肪率 —" : `体脂肪率 ${Number(item.body_fat_percent).toFixed(1)}%`}</small>
                {item.notes && <p>{item.notes}</p>}
              </article>
            ))}
          </div>
        )}
      </section>

      <ActivityHistory
        activities={filteredActivities}
        onDeleted={(id) => setActivities((current) => current.filter((item) => item.id !== id))}
        onMessage={setMessage}
        onError={setError}
      />
    </>
  );
}
